import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiCalendar, FiHeart, FiShare2, FiMail, FiAward, FiBookOpen, FiUsers } = FiIcons;

const TeacherProfile = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('active');

  // Mock teacher data with Jordanian location
  const teacher = {
    id: 1,
    name: 'Sarah Johnson',
    school: 'Pine Valley Elementary',
    location: 'Amman, Jordan',
    subjects: ['Science', 'Math', 'Environmental Studies'],
    grades: ['3rd', '4th', '5th'],
    bio: 'I am passionate about making STEM accessible to all students through hands-on learning experiences. With over 8 years of teaching experience, I believe in creating an inclusive classroom where every student can discover their potential in science and mathematics. My goal is to inspire the next generation of innovators and problem-solvers.',
    photo: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400',
    joinDate: 'September 2022',
    totalRaised: 15420,
    studentsImpacted: 247,
    projectsCompleted: 12,
    activeProjects: 3,
    achievements: [
      'Excellence in STEM Education Award 2023',
      'Teacher of the Year Nominee 2022',
      'Environmental Education Champion'
    ]
  };

  const activeProjects = [
    {
      id: 1,
      title: 'STEM Lab Equipment for Hands-On Learning',
      description: 'Help us create an engaging STEM lab with microscopes, circuit kits, and robotics materials.',
      goal: 2500,
      raised: 1850,
      daysLeft: 23,
      image: 'https://images.unsplash.com/photo-1581833971358-2c8b550f87b3?w=400',
      category: 'Science'
    },
    {
      id: 2,
      title: 'Outdoor Learning Garden Project',
      description: 'Transform our unused courtyard into an outdoor classroom and vegetable garden.',
      goal: 1800,
      raised: 920,
      daysLeft: 45,
      image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400',
      category: 'Environmental Studies'
    },
    {
      id: 3,
      title: 'Math Manipulatives for Visual Learning',
      description: 'Provide hands-on math tools to help students visualize complex mathematical concepts.',
      goal: 800,
      raised: 340,
      daysLeft: 32,
      image: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=400',
      category: 'Math'
    }
  ];

  const completedProjects = [
    {
      id: 4,
      title: 'Reading Corner Book Collection',
      description: 'Created a cozy reading corner with diverse books for all reading levels.',
      goal: 600,
      raised: 750,
      completedDate: 'March 2024',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
      impact: 'Increased reading engagement by 40% in our classroom.'
    },
    {
      id: 5,
      title: 'Science Fair Display Materials',
      description: 'Provided materials for students to create impressive science fair projects.',
      goal: 400,
      raised: 480,
      completedDate: 'January 2024',
      image: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=400',
      impact: '3 students won awards at the district science fair!'
    }
  ];

  const testimonials = [
    {
      text: "Ms. Johnson's innovative teaching methods have sparked my daughter's love for science. The hands-on experiments make learning so engaging!",
      author: "Parent of Emma (4th grade)",
      date: "February 2024"
    },
    {
      text: "Thanks to the STEM equipment funded through this platform, our students are conducting real experiments and thinking like scientists.",
      author: "Principal Martinez",
      date: "January 2024"
    },
    {
      text: "The outdoor garden project has transformed how we learn about plants and ecosystems. It's amazing to see science come alive!",
      author: "Student - Alex (5th grade)",
      date: "March 2024"
    }
  ];

  const ProjectCard = ({ project, isCompleted = false }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
    >
      <img
        src={project.image}
        alt={project.title}
        className="w-full h-48 object-cover"
      />
      <div className="p-6">
        <div className="flex items-center justify-between mb-2">
          <span className="px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded-full">
            {project.category || 'Education'}
          </span>
          {isCompleted && (
            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
              Completed
            </span>
          )}
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">{project.title}</h3>
        <p className="text-gray-600 mb-4">{project.description}</p>

        {!isCompleted ? (
          <>
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>${project.raised} raised</span>
                <span>${project.goal} goal</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-500 h-2 rounded-full"
                  style={{ width: `${(project.raised / project.goal) * 100}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-500 mt-2">{project.daysLeft} days left</p>
            </div>
            <div className="flex space-x-3">
              <Link
                to={`/project/${project.id}`}
                className="flex-1 bg-primary-500 text-white text-center py-2 rounded-lg hover:bg-primary-600 transition-colors"
              >
                View Details
              </Link>
              <Link
                to={`/donate/${project.id}`}
                className="flex-1 border border-primary-500 text-primary-500 text-center py-2 rounded-lg hover:bg-primary-50 transition-colors"
              >
                Donate Now
              </Link>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Completed: {project.completedDate}</span>
              <span className="text-green-600 font-semibold">${project.raised} raised</span>
            </div>
            {project.impact && (
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-green-800 text-sm font-medium">Impact:</p>
                <p className="text-green-700 text-sm">{project.impact}</p>
              </div>
            )}
            <Link
              to={`/project/${project.id}`}
              className="block w-full bg-gray-100 text-gray-700 text-center py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              View Project
            </Link>
          </div>
        )}
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Teacher Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-start space-y-6 md:space-y-0 md:space-x-8">
            {/* Profile Photo */}
            <motion.img
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              src={teacher.photo}
              alt={teacher.name}
              className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
            />

            {/* Teacher Info */}
            <div className="flex-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{teacher.name}</h1>
                <p className="text-xl text-gray-600 mb-2">{teacher.school}</p>
                <p className="text-gray-500 flex items-center mb-4">
                  <SafeIcon icon={FiMapPin} className="mr-2" />
                  {teacher.location}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {teacher.subjects.map((subject) => (
                    <span
                      key={subject}
                      className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
                    >
                      {subject}
                    </span>
                  ))}
                  {teacher.grades.map((grade) => (
                    <span
                      key={grade}
                      className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-sm"
                    >
                      {grade}
                    </span>
                  ))}
                </div>
                <p className="text-gray-500 flex items-center">
                  <SafeIcon icon={FiCalendar} className="mr-2" />
                  Teaching since {teacher.joinDate}
                </p>
              </motion.div>
            </div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-col space-y-3"
            >
              <button className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors flex items-center space-x-2">
                <SafeIcon icon={FiHeart} />
                <span>Follow Teacher</span>
              </button>
              <button className="border border-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2">
                <SafeIcon icon={FiShare2} />
                <span>Share Profile</span>
              </button>
              <button className="border border-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2">
                <SafeIcon icon={FiMail} />
                <span>Contact</span>
              </button>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Bio Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-4">About {teacher.name.split(' ')[0]}</h2>
              <p className="text-gray-700 leading-relaxed">{teacher.bio}</p>
            </motion.div>

            {/* Projects Tabs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-white rounded-xl shadow-sm"
            >
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6">
                  <button
                    onClick={() => setActiveTab('active')}
                    className={`py-4 border-b-2 font-medium text-sm ${
                      activeTab === 'active'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Active Projects ({teacher.activeProjects})
                  </button>
                  <button
                    onClick={() => setActiveTab('completed')}
                    className={`py-4 border-b-2 font-medium text-sm ${
                      activeTab === 'completed'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Completed Projects ({teacher.projectsCompleted})
                  </button>
                </nav>
              </div>

              <div className="p-6">
                {activeTab === 'active' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {activeProjects.map((project) => (
                      <ProjectCard key={project.id} project={project} />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {completedProjects.map((project) => (
                      <ProjectCard key={project.id} project={project} isCompleted={true} />
                    ))}
                  </div>
                )}
              </div>
            </motion.div>

            {/* Testimonials & Impact */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Impact Stories & Testimonials</h2>
              <div className="space-y-6">
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 italic mb-3">"{testimonial.text}"</p>
                    <div className="flex justify-between text-sm">
                      <span className="font-medium text-gray-900">{testimonial.author}</span>
                      <span className="text-gray-500">{testimonial.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Stats Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Teaching Impact</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiHeart} className="text-primary-500" />
                    <span className="text-gray-600">Total Raised</span>
                  </div>
                  <span className="font-semibold text-gray-900">${teacher.totalRaised.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiUsers} className="text-primary-500" />
                    <span className="text-gray-600">Students Impacted</span>
                  </div>
                  <span className="font-semibold text-gray-900">{teacher.studentsImpacted}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiBookOpen} className="text-primary-500" />
                    <span className="text-gray-600">Projects Completed</span>
                  </div>
                  <span className="font-semibold text-gray-900">{teacher.projectsCompleted}</span>
                </div>
              </div>
            </motion.div>

            {/* Achievements */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <SafeIcon icon={FiAward} className="mr-2" />
                Achievements
              </h3>
              <div className="space-y-3">
                {teacher.achievements.map((achievement, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                    <span className="text-gray-700 text-sm">{achievement}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Support CTA */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-sm p-6 text-white"
            >
              <h3 className="text-lg font-semibold mb-2">Support {teacher.name.split(' ')[0]}'s Classroom</h3>
              <p className="text-primary-100 text-sm mb-4">
                Help bring innovative learning experiences to students by supporting ongoing projects.
              </p>
              <button className="w-full bg-white text-primary-600 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                View All Projects
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherProfile;